import { QueryKeys } from '@/enums';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import {  IGetCartProduct } from '@/interfaces/cart';
import { IBopisOrderStatus,IEmiInfo, IOrderDetails, IOrders, IPaymentType, IPurchaseHistory, IRequests, IReturnExchangeInitialization, IReturnExchangeRequestDetails, IStatusTimeline } from '@/interfaces/orders';
import { cancelReturnExchange,getBopisOrderStatus, getEMIInfo, getOrderDetails, getOrderList, getOrderSummery, getPaymentType, getPurchaseHistory, getReturnExchangeList, giftVoucher, initializeReturnExchange, reOrder, requestReturnExchange, sendCorporateDiscountOtp, submitReturnExchange } from '@/services/order.service';
import useHeaderStore from '@/store/header';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { processCoupon } from '@/helpers/order-helper';
import { useOrderStore } from '@/store/order';
import { createQueryKey } from '@/helpers/create-querykey';

/**
 * Fetch the user's available payment options.
 * @param isBopis        is BOPIS checkout (store pickup)
 * @param isExpress      is Express delivery
 * @param cartVersion    unused cart-version pin (pass `undefined` to skip)
 * @param discountId     campaign discount id to filter by (e.g. TierBased)
 * NOTE: positional args; pass `undefined` for any skipped parameter to
 * avoid arg-shift. Example: `useGetPaymentType(true, undefined, undefined, 'discount-xyz')`.
 */
export const useGetPaymentType = (isBopis?: boolean | null, isExpress?: boolean | null, cartVersion?: string | number, discountId?: string) => {
  const auth = useReactQuerySession();
  return useQuery<IPaymentType[]>({
    queryKey: [QueryKeys.getPaymentType, isBopis, isExpress, cartVersion, discountId],
    queryFn: async () => await getPaymentType(auth?.session, undefined, isBopis, isExpress, discountId),
    enabled: !!auth?.session,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchOnMount: true,
  });
};

export const useOrderSummary = (
  OrderDeliveryType: number |undefined,
  id?: string,
  StoreIds?: string[],
  session?: INextAuthSessionModel | null,
  callOrderDetails?: boolean,
  PointsToRedeem?: number | null,
  coupon?: string,
  setShowOTPField?: Function,
  setDisableOrderButton?: Function,
  setPoints?: Function,
  setRedeemedCoupon?: Function,
  setRedeemedPoints?: Function,
  clickedButton?: 'Redeem' | 'Coupon' | '',
  PhoneNumber?: string,
  setNumber?: Function,
  setLoading?: Function,
  BankId?: string,
  BankEmiId?: string,
  setShowRemoveOption?: Function,
  setCoupon?: Function,
  corporateDiscountOtp?: string,
  removeCorporateDiscount?: boolean,
  CouponId?: string,
  isObjectivebasedCampaignClaimed?: boolean,
) => {
 const storeIds =
  StoreIds?.length && StoreIds[0] !== ''
    ? StoreIds.map(String)
    : [];
  let key: string | undefined = '';

  // NOTE: gift-voucher and coupon has been merged into "coupon" as per new requirement.
  if (PointsToRedeem && clickedButton === 'Redeem') {
    key = PointsToRedeem.toString();
  } else if (coupon && clickedButton === 'Coupon') {
    key = coupon;
  } else if (coupon === '' || PointsToRedeem === null) {
    key = clickedButton;
  }

  const queryKey = createQueryKey(QueryKeys.orderSummary,id, OrderDeliveryType, key, BankId, ...storeIds, BankEmiId, PhoneNumber, corporateDiscountOtp, removeCorporateDiscount, CouponId, isObjectivebasedCampaignClaimed);
  // Helper function to handle success cases
const handleSuccessResponse = (res: IGetCartProduct, PointsToRedeem: number|null|undefined, clickedButton?: string, coupon?: string) => {
  if (PointsToRedeem && clickedButton === 'Redeem') {
    return handleRedeemResponse(res);
  }
  
  if (Boolean(coupon) && clickedButton === 'Coupon') {
    return handleCouponResponse(res);
  }
  
  return null;
};

// Handle redeem-specific logic
const handleRedeemResponse = (res: IGetCartProduct) => {
  if (res.Warnings && res.Warnings.length > 0) {
    openNotification('danger', 'Reward Point', res.Warnings.join(', ') + '.');
    setPoints?.('');
    setRedeemedPoints?.(null);
    return;
  }
  
  openNotification('success', 'Reward Point', 
    'A verification code was sent to your number, please use the code to confirm your order'
  );
  setShowOTPField?.(true);
  setDisableOrderButton?.(true);
};

// Handle coupon-specific logic
const handleCouponResponse = (res: IGetCartProduct) => {
  processCoupon({
    setRedeemedCoupon,
    setLoading,
    setShowRemoveOption,
    res,
    setCoupon
  });
};

// Extract error handling
const extractErrorMessage = (error: any) => {
  const res = error;
  
  if (res.errors) {
    return Object.values(res.errors).join(', ') + '.';
  }
  
  if (res.Messages) {
    return res.Messages[0];
  }
  
  if (res?.data?.Messages) {
    return res.data.Messages[0];
  }
  
  return 'An unknown error occurred.';
};

  const query = useQuery<IGetCartProduct>({
    queryKey,
    queryFn: async () => {
      return await getOrderSummery(
        OrderDeliveryType,
        id,
        StoreIds,
        session,
        undefined,
        PointsToRedeem,
        coupon,
        PhoneNumber,
        BankId,
        BankEmiId,
        corporateDiscountOtp,
        removeCorporateDiscount,
        CouponId,
        isObjectivebasedCampaignClaimed,
      );
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: callOrderDetails && !!session && OrderDeliveryType !== undefined,
    placeholderData: (previousData) => previousData, // keepPreviousData was renamed to placeholderData in v5
    gcTime: queryKey.length > 1 ? 0 : 1000 * 60 * 5, // cacheTime was renamed to gcTime in v5
  });

  // Handle success/error effects
  useEffect(() => {
  if (query.isSuccess && query.data) {
    const res = query.data;
    handleSuccessResponse(res, PointsToRedeem, clickedButton, coupon);
    return;
  }
  
  if (query.isError && query.error) {
    const errorMessage = extractErrorMessage(query.error);
    openNotification('danger', 'Order Summary', errorMessage);
  }
}, [
 query.isSuccess,
    query.isError,
    query.data,
    query.error
]);

  return query;
};

export const useGetOrders = (enabled: boolean = true ) => {
  const auth = useReactQuerySession();
  return useQuery<IOrders>({
    queryKey: [QueryKeys.getOrders],
    queryFn: async () => {
      return await getOrderList(auth?.session as INextAuthSessionModel);
    },
    enabled: !!auth?.session && enabled,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });
};

export const useGetPurchaseHistory = (pageNumber: number, pageSize: number) => {
  const auth = useReactQuerySession();
  return useQuery<IPurchaseHistory>({
    queryKey: [QueryKeys.purchaseHistory, pageNumber, pageSize],
    queryFn: async () => {
      return await getPurchaseHistory(auth?.session, pageNumber, pageSize);
    },
    enabled: !!auth?.session && pageNumber > 0,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });
};

export const useGetReturnExchange = (pageIndex: number, pageSize: number, requestType?: number) => {
  const auth = useReactQuerySession();
  return useQuery<IRequests>({
    queryKey: [QueryKeys.returnExchange, requestType],
    queryFn: async () => {
      return await getReturnExchangeList(auth?.session, pageSize, pageIndex, requestType);
    },
    enabled: !!auth?.session && requestType !== undefined,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });
};

export const useReturnExchange = (orderCode: string, requestType: number) => {
  const auth = useReactQuerySession();

  return useQuery<IReturnExchangeInitialization>({
    queryKey: [QueryKeys.returnExchange, orderCode, requestType],
    queryFn: async () => {
      return await initializeReturnExchange(
        orderCode,
        requestType,
        auth?.session as INextAuthSessionModel
      );
    },
    enabled: !!auth?.session && !!orderCode && !!requestType,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });
};

export const useReturnExchangeRequest = (requestCode: string) => {
  const auth = useReactQuerySession();

  return useQuery<IReturnExchangeRequestDetails>({
    queryKey: [QueryKeys.returnExchange, requestCode],
    queryFn: async () => {
      return await requestReturnExchange(
        requestCode,
        auth?.session as INextAuthSessionModel
      );
    },
    enabled: !!auth?.session && !!requestCode,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });
};

/**
 * Hook for submitting a Return/Exchange request.
 * @param onUploadProgress optional callback receiving (loaded, total) bytes during the multipart upload.
 *                        Caller maps to a percentage; the hook stays percent-agnostic.
 */
export const useSubmitReturnExchange = (
  requestType: number,
  orderCode: string,
  onUploadProgress?: (loaded: number, total: number) => void,
) => {
  const router = useRouter();
  const setSelectedProductsForReturn = useOrderStore((state) => state.setSelectedProductsForReturn);
  return useMutation({
    mutationFn: (formData: FormData) => submitReturnExchange(formData, onUploadProgress),
    onSuccess: (data) => {
      if (requestType === 1)
        router.push(`/account/order-history/return?status=success&code=${data?.Code}&orderCode=${orderCode}`)
      else router.push(`/account/order-history/exchange?status=success&code=${data?.Code}&orderCode=${orderCode}`)
      openNotification('success', 'Submitted Successfully', data?.Message)

    },
    onError: (error: any) => {
      const errorMessage = error?.data?.Messages?.[0] || 'Failed to submit return/exchange request';
      openNotification('danger', 'Submission Error', errorMessage);
      setSelectedProductsForReturn([]);

    },
  });
};

export const useGeTOrderDetails = (orderCode: string, isPendingItemAvailable: boolean, setIsPendingItemAvailable: Function) => {
  const auth = useReactQuerySession();
  const query = useQuery<IOrderDetails>({
    queryKey: [QueryKeys.getOrderDetails, orderCode],
    queryFn: async () => {
      const data = await getOrderDetails(orderCode, auth?.session as INextAuthSessionModel);
      data?.SubOrders?.forEach((subOrder) => {
        subOrder?.StatusTimeline?.forEach((status: IStatusTimeline) => {
          status.date = dayjs(status?.CreatedOn).format('DD-MM-YY');
          status.time = dayjs(status?.CreatedOn).format('hh:mm A');
        });
        let timeline = subOrder?.StatusTimeline?.filter((status: IStatusTimeline) => !status?.Shadow);
        subOrder.timelineMessage = [...timeline]?.reverse();
      });
      return data;
    },
    enabled: !!auth?.session,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchOnMount: false,
    // refetchInterval: isPendingItemAvailable ? 5000 : false   due to not understanding when an order will finish we are keeping the interval 5 seconds,
    refetchInterval: 5000,
  });

  // Handle success effect
  useEffect(() => {
    if (query.isSuccess && query.data) {
      setIsPendingItemAvailable?.(!!query.data.OrderItems.length);
    }
  }, [query.isSuccess, query.data, setIsPendingItemAvailable]);

  return query;
};



export const useOrderTracker = (orderCode: string) => {
  const setOrderTrackerLoading = useHeaderStore((state) => state.setOrderTrackerLoading);
  const router = useRouter();
  const auth = useReactQuerySession();

  const query = useQuery<IOrderDetails>({
    queryKey: [QueryKeys.getOrderDetails, orderCode],
    queryFn: async () => {
      if (orderCode) {
        setOrderTrackerLoading(true);
      }

      return await getOrderDetails(orderCode, auth?.session as INextAuthSessionModel);
    },
    enabled: !!auth?.session,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchOnMount: false,
  });

  // Handle success/error effects
  useEffect(() => {
    if (query.isSuccess && query.data) {
      const data = query.data;
      if (orderCode && data.OrderCode != null) {
        router.replace(`/account/order-details/${data.OrderCode}`);
        setOrderTrackerLoading(false);
      } else if (orderCode && data.OrderCode == null) {
        openNotification('danger', 'Tracking Failed', `Order ${orderCode} does not exist.`);
        setOrderTrackerLoading(false);
      }
    } else if (query.isError) {
      setOrderTrackerLoading(false);
      openNotification('danger', 'Tracking Failed', `Order ${orderCode} does not exist.`);
    }
  }, [query.isSuccess, query.isError, query.data, orderCode, router, setOrderTrackerLoading]);

  return query;
};

export const useGiftVoucher = (triggerVoucherCall: boolean, VoucherCoupon?: string, session?: INextAuthSessionModel | null) => {
  const queryClient = useQueryClient();

  const query = useQuery<any>({
    queryKey: [QueryKeys.voucher],
    queryFn: async () => {
      return await giftVoucher(VoucherCoupon, session);
    },
    enabled: !!triggerVoucherCall,
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });

  // Handle success/error effects
  useEffect(() => {
    if (query.isSuccess) {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.orderSummary] });
      openNotification('success', 'Voucher', 'Voucher has been redeemed successfully');
    } else if (query.isError && query.error) {
      const data = query.error as any;
      openNotification('danger', 'Voucher', data.data.Messages[0]);
    }
  }, [query.isSuccess, query.isError, query.error, queryClient]);

  return query;
};

export const useReOrder = (setOpened: Function, orderCode?: string) => {
  const router = useRouter();
  return useMutation({
    mutationFn: reOrder,
    onSuccess: (data) => {
      if (data.ItemCount > 0) {
        openNotification('success', 'Order', 'Re-order process initiated successfully');
        setOpened(false);
        
        router.push(`/checkout/details/home-delivery?orderCode=${orderCode}`);
      } else {
        openNotification('danger', 'Order', 'Re-order failed, products out of stock');
        setOpened(false);
      }
    },
    onError: (data: any) => {
      openNotification('danger', 'Order', 'Re-order failed, products out of stock');
      setOpened(false);
    },
  });
};

export const useCancelReturnExchange = (
  requestType?: number
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      requestCode: string;
      orderItemIds: string[];
      cancellationReason: string;
    }) => cancelReturnExchange(payload.requestCode, payload.orderItemIds, payload.cancellationReason),
    onSuccess: (data) => {
      openNotification('success', 'Return/Exchange', 'Cancellation request processed successfully');
      queryClient.invalidateQueries({ queryKey: [QueryKeys.returnExchange, requestType] });

    },
    onError: (data: any) => {
      openNotification('danger', 'Return/Exchange', 'Cancellation failed. Please try again.');
    },
  });
};

export const useGetEmiInfo = () => {
  return useQuery<IEmiInfo[]>({
    queryKey: [QueryKeys.emiInfo],
    queryFn: async () => {
      const emiInfo: IEmiInfo[] = await getEMIInfo();

      // Sort the EmiInfos array by Tenure in ascending order
      const sortedBanksData = emiInfo.map((bank) => {
        const sortedBank = { ...bank };

        if (sortedBank.EmiInfos && sortedBank.EmiInfos.length > 0) {
          sortedBank.EmiInfos = [...sortedBank.EmiInfos].sort((a, b) => a.Tenure - b.Tenure);
        }

        return sortedBank;
      });

      return sortedBanksData;
    },
    refetchOnWindowFocus: false,
    retry: false,
    refetchOnMount: false,
  });
};

export const useGeTOrderDetailsForOrderTypeChange = (
  orderCode: string,
  isSwitchToHomeDelivery?: boolean,
  shippingAddressId?: string,
  billingAddressId?: string,
) => {
  const auth = useReactQuerySession();

  return useQuery<IOrderDetails>({
    queryKey: [QueryKeys.getOrderDetailsForDeliveryTypeChange, orderCode, shippingAddressId, billingAddressId],
    queryFn: async () => {
      const data = await getOrderDetails(
        orderCode,
        auth?.session as INextAuthSessionModel,
        isSwitchToHomeDelivery,
        shippingAddressId,
        billingAddressId,
      );

      data?.SubOrders?.forEach((subOrder) => {
        subOrder?.StatusTimeline?.forEach((status: IStatusTimeline) => {
          status.date = dayjs(status?.CreatedOn).format('DD-MM-YY');
          status.time = dayjs(status?.CreatedOn).format('hh:mm A');
        });

        const timeline = subOrder?.StatusTimeline?.filter((status: IStatusTimeline) => !status?.Shadow);
        subOrder.timelineMessage = [...(timeline || [])]?.reverse();
      });

      return data;
    },
    enabled: Boolean(orderCode) && Boolean(auth?.session),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const useSendCorporateDiscountOtpMutation = () => {
  const auth = useReactQuerySession();

  return useMutation({
    mutationFn: () => sendCorporateDiscountOtp(auth?.session),
    onError: (error: any) => {
      openNotification('danger', 'Email Verification', error.data?.Messages?.[0] || error.data?.Exception || 'Failed to send OTP. Please try again.');
    },
    onSuccess: () => {
      openNotification('success', 'Email Verification', 'OTP sent successfully');
    },

  });
};
export const useGetBopisOrderStatus = () => {
  const auth = useReactQuerySession();

  return useQuery<IBopisOrderStatus[]>({
    queryKey: [QueryKeys.bopisOrderStatus],
    queryFn: async () => {
      return await getBopisOrderStatus(
        auth?.session as INextAuthSessionModel,
      );
    },
    enabled: !!auth?.session,
    gcTime: 1000 * 60 * 3, // 3 minutes
    retry: false,


  });
};
